// @flow

import * as React from "react";
import { withRouter } from "react-router-dom";
import { Form } from "react-final-form";

import Button from "../../components/Button";
import Card from "../../components/Card";
import FieldControl from "../../components/FieldControl";
import { FlashMessageConsumer } from "../../components/FlashMessages";
import FormRow from "../../components/FormRow";
import PageHeader from "../../components/PageHeader";
import PageLayout from "../../components/PageLayout";
import PasswordInput from "../../components/PasswordInput";

import { resetPassword } from "../../api/actions";
import * as validators from "../../utils/validators";
import { errors } from "../../utils/errors";
import { isFormSubmitting } from "../../utils/helpers";

const PASSWORD = "password";
const PASSWORD_CONFIRMATION = "password_confirmation";

type NewPasswordProps = {
  match: {
    params: {
      token: string
    }
  },
  loginPath: string,
  passwordRegex: RegExp,
  passwordError: string
};

type NewPasswordState = {|
  sent: boolean
|};

class NewPassword extends React.Component<NewPasswordProps, NewPasswordState> {
  state = {
    sent: false,
    isPasswordErrorShowed: false
  };

  mapFormErrors = (errorResponse: { data: { errors?: { type: string }[] } }) => {
    const { errors: errs } = errorResponse.data;
    if (!errs) {
      return;
    }

    errs.forEach(e => {
      if (e.type === "password_already_used") {
        this.setState({ isPasswordErrorShowed: true });
      }
    });
  };

  submitForm =
    (addMessage: (React.Node, "success" | "failure") => void) =>
    (values: { password: string, password_confirmation: string }) => {
      const { match } = this.props;
      this.setState({ isPasswordErrorShowed: false });

      return resetPassword({
        reset_password_token: match.params.token,
        ...values
      })
        .then(() => {
          this.setState({ sent: true });
        })
        .catch((err: any) => {
          if (err && err.response && err.response.status) {
            if (err.response.status === 401) {
              addMessage(
                "Dieser Link ist leider abgelaufen und/oder wurde bereits zur Zurücksetzung des Passworts benutzt. Sie können den Prozess jederzeit neu anstoßen.",
                "failure"
              );
            }

            if (err.response.status === 422) {
              this.mapFormErrors(err.response);
            }
          }
        });
    };

  render() {
    const { sent, isPasswordErrorShowed } = this.state;
    const { loginPath, passwordRegex, passwordError } = this.props;

    return (
      <PageLayout>
        <PageHeader header="Passwort zurücksetzen" bottomMargin />
        <Card>
          {sent ? (
            <>
              <FormRow>
                <header>
                  Vielen Dank! Ihr Passwort wurde erfolgreich zurückgesetzt. Sie können sich wie
                  gewohnt anmelden.
                </header>
              </FormRow>
              <Button size="large" asRouterLink href={loginPath}>
                Jetzt anmelden
              </Button>
            </>
          ) : (
            <>
              <header>
                Bitte geben Sie ein neues Passwort an und bestätigen Sie dieses. Anschließend können
                Sie sich mit Ihrem neuen Passwort wie gewohnt anmelden.
              </header>
              <FlashMessageConsumer>
                {({ addMessage }) => (
                  <Form onSubmit={this.submitForm(addMessage)}>
                    {({ form }) => (
                      <form onSubmit={() => false}>
                        <FormRow>
                          <FieldControl
                            name={PASSWORD}
                            caption={() => "Passwort"}
                            validate={validators.combineValidators(
                              validators.required(errors.requiredField()),
                              validators.isPassword(passwordRegex, passwordError)
                            )}
                            validationMessage={
                              isPasswordErrorShowed ? errors.isAlreadyUsedPassword() : null
                            }
                          >
                            {({ input }) => (
                              <PasswordInput
                                input={input}
                                placeholder="Geben Sie Ihr Passwort an..."
                              />
                            )}
                          </FieldControl>
                          <FieldControl
                            name={PASSWORD_CONFIRMATION}
                            caption={() => "Passwort bestätigen"}
                            validate={validators.combineValidators(
                              validators.required(errors.requiredField()),
                              validators.equalToField("password", errors.passwordMatch())
                            )}
                          >
                            {({ input }) => (
                              <PasswordInput
                                input={input}
                                placeholder="Geben Sie erneut Ihr Passwort an..."
                              />
                            )}
                          </FieldControl>
                        </FormRow>
                        <Button
                          data-testid="password-reset-button"
                          isDisabled={!form.getState().valid || isFormSubmitting(form)}
                          onClick={form.submit}
                        >
                          Passwort zurücksetzen
                        </Button>
                      </form>
                    )}
                  </Form>
                )}
              </FlashMessageConsumer>
            </>
          )}
        </Card>
      </PageLayout>
    );
  }
}

export default withRouter(NewPassword);
